import React, { useState } from "react";
import "./AuthModal.css";
import { useUser } from "../context/UserContext";
import { useApi } from "../utils/Api";

// Spinner overlay
const SpinnerOverlay = () => (
  <div className="spinner-overlay">
    <div className="spinner"></div>
  </div>
);

const AuthModal = ({ onClose }) => {
  const { setUser } = useUser();
  const { apiFetch } = useApi();

  const [mode, setMode] = useState("login");
  const [showSignupForm, setShowSignupForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Login
  const [loginPhone, setLoginPhone] = useState("");
  const [loginCountry, setLoginCountry] = useState("+44");

  // Signup
  const [signupFirstName, setSignupFirstName] = useState("");
  const [signupLastName, setSignupLastName] = useState("");
  const [signupPhone, setSignupPhone] = useState("");
  const [signupCountry, setSignupCountry] = useState("+44");
  const [signupEmail, setSignupEmail] = useState("");

  // Welcome
  const [welcomeScreen, setWelcomeScreen] = useState(false);
  const [loginUser, setLoginUser] = useState(null);

  const isValidPhoneLength = (phone) => {
    const digits = String(phone).replace(/\D/g, "");
    return digits.length >= 10 && digits.length <= 11;
  };

  const goToLogin = () => {
    setError("");
    setMode("login");
    setShowSignupForm(false);
  };

  const goToSignup = () => {
    setError("");
    setMode("signup");
    setShowSignupForm(false);
  };

  const goToSignupForm = () => {
    setError("");
    setShowSignupForm(true);
  };

  // ---------------- LOGIN ----------------
  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    if (!isValidPhoneLength(loginPhone)) {
      setError("Please enter a valid phone number");
      return;
    }

    setLoading(true);

    try {
      const fullPhone = loginCountry + loginPhone;
      const data = await apiFetch("customer-login", { phone: fullPhone });

      setLoading(false);

      if (data.status === "success") {
        const userWithToken = { ...data.customer, token: data.token };

        setUser(userWithToken);
        setLoginUser(userWithToken);

        localStorage.setItem("user", JSON.stringify(userWithToken));
        localStorage.setItem("authTokenExpires", data.expiresAt);

        setWelcomeScreen(true);

        setTimeout(() => {
          setWelcomeScreen(false);
          onClose();
        }, 2000);
      } else {
        setError("User does not exist. Sign up instead.");
      }
    } catch {
      setError("Something went wrong.");
      setLoading(false);
    }
  };

  // ---------------- SIGNUP ----------------
  const handleSignup = async (e) => {
    e.preventDefault();
    setError("");

    if (!isValidPhoneLength(signupPhone)) {
      setError("Please enter a valid phone number");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        first_name: signupFirstName,
        last_name: signupLastName,
        phone: signupCountry + signupPhone,
        email: signupEmail,
      };

      const data = await apiFetch("customer-signup", payload);
      setLoading(false);

      if (data.status === "success") {
        const userWithToken = {
          ...data.customer,
          token: data.token,
        };

        setUser(userWithToken);
        setLoginUser(userWithToken);

        localStorage.setItem("user", JSON.stringify(userWithToken));
        localStorage.setItem("authTokenExpires", data.expiresAt);

        setWelcomeScreen(true);

        setTimeout(() => {
          setWelcomeScreen(false);
          onClose();
        }, 2000);
      } else {
        setError(data.error || "Signup failed");
      }
    } catch {
      setError("Something went wrong.");
      setLoading(false);
    }
  };

  return (
    <div className="auth-modal-overlay">
      {loading && <SpinnerOverlay />}
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <span className="auth-modal-close" onClick={onClose}>
          &times;
        </span>

        {welcomeScreen && loginUser ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <h2>Welcome, {loginUser.first_name || "Guest"}!</h2>
            <p style={{ marginTop: "10px" }}>You're now logged in.</p>
          </div>
        ) : (
          <>
            {mode === "login" ? (
              <form onSubmit={handleLogin} className="auth-form">
                <div className="auth-modal-header">
                  <h2>Welcome Back!</h2>
                  <p>Login with your phone number.</p>
                </div>

                <div className="phone-input-wrapper">
                  <select
                    value={loginCountry}
                    onChange={(e) => setLoginCountry(e.target.value)}
                  >
                    <option value="+44">+44</option>
                    <option value="+234">+234</option>
                  </select>

                  <input
                    type="tel"
                    placeholder="Phone Number"
                    value={loginPhone}
                    maxLength={11}
                    onChange={(e) =>
                      setLoginPhone(e.target.value.replace(/\D/g, ""))
                    }
                    required
                  />
                </div>

                {error && <p className="auth-error">{error}</p>}

                <button type="submit">Login</button>

                <p className="switch-mode">
                  Don’t have an account?{" "}
                  <span className="clickable-link" onClick={goToSignup}>
                    Sign Up
                  </span>
                </p>
              </form>
            ) : !showSignupForm ? (
              <div className="signup-benefits">
                <h2>Why Sign Up?</h2>
                <ul>
                  <li>📦 View all your previous orders</li>
                  <li>📍 Track your orders in real time</li>
                  <li>🔄 Reorder your previous orders in one click</li>
                  <li>🎁 Receive updates on promos and discounts</li>
                </ul>

                <button
                  className="proceed-signup-btn"
                  onClick={goToSignupForm}
                >
                  Proceed to Sign Up
                </button>

                <p className="switch-mode">
                  Already have an account?{" "}
                  <span className="clickable-link" onClick={goToLogin}>
                    Login
                  </span>
                </p>
              </div>
            ) : (
              <form onSubmit={handleSignup} className="auth-form">
                <div className="auth-modal-header">
                  <h2>Create Account</h2>
                  <p>Sign up to get started.</p>
                </div>

                <input
                  value={signupFirstName}
                  onChange={(e) => setSignupFirstName(e.target.value)}
                  placeholder="First Name"
                  required
                />

                <input
                  value={signupLastName}
                  onChange={(e) => setSignupLastName(e.target.value)}
                  placeholder="Last Name"
                  required
                />

                <div className="phone-input-wrapper">
                  <select
                    value={signupCountry}
                    onChange={(e) => setSignupCountry(e.target.value)}
                  >
                    <option value="+44">+44</option>
                    <option value="+234">+234</option>
                  </select>

                  <input
                    type="tel"
                    value={signupPhone}
                    maxLength={11}
                    onChange={(e) =>
                      setSignupPhone(e.target.value.replace(/\D/g, ""))
                    }
                    placeholder="Phone Number"
                    required
                  />
                </div>

                <input
                  type="email"
                  value={signupEmail}
                  onChange={(e) => setSignupEmail(e.target.value)}
                  placeholder="Email"
                  required
                />

                {error && <p className="auth-error">{error}</p>}

                <button type="submit">Sign Up</button>

                <p className="switch-mode">
                  Already have an account?{" "}
                  <span className="clickable-link" onClick={goToLogin}>
                    Login
                  </span>
                </p>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AuthModal;